'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import { usePharmacyBookingStore } from '@/features/pharamacy/store/usePharmacyBookingStore';
import { pushGtmEvent } from '@/lib/analytics/gtm';
import { GTM_EVENTS } from '@/lib/analytics/gtmEvents';

const PharmacyBookingModal = dynamic(
  () =>
    import('@/features/pharamacy/components/PharmacyBookingFlow/PharmacyBookingModal'),
  { ssr: false },
);

interface PharmacyBookingModalContextValue {
  openModal: (serviceId?: number) => void;
  closeModal: () => void;
}

const PharmacyBookingModalContext =
  createContext<PharmacyBookingModalContextValue | null>(null);

export function PharmacyBookingModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [serviceId, setServiceId] = useState<number | undefined>(undefined);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  const openModal = useCallback((svcId?: number) => {
    setServiceId(svcId);
    setIsOpen(true);
    pushGtmEvent(GTM_EVENTS.REACH_PHARMACY_BOOKING_BY);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setServiceId(undefined);
    usePharmacyBookingStore.getState().resetBookingModalFlow();
  }, []);

  return (
    <PharmacyBookingModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {isOpen &&
        portalTarget &&
        createPortal(
          <PharmacyBookingModal
            isOpen={isOpen}
            onClose={closeModal}
            initialServiceId={serviceId}
          />,
          portalTarget,
        )}
    </PharmacyBookingModalContext.Provider>
  );
}

export function usePharmacyBookingModal(): PharmacyBookingModalContextValue {
  const ctx = useContext(PharmacyBookingModalContext);
  if (!ctx) {
    throw new Error(
      'usePharmacyBookingModal must be used within PharmacyBookingModalProvider',
    );
  }
  return ctx;
}
